import { Injectable } from '@angular/core';
import { LoggingService } from './logging.service';
import { wrapAllMethodsWithLogging } from 'src/app/core/decorators/log-all-errors.decorator';


// Update outcome type to cover all terminal states
export type CaptureOutcome =
  | 'success'          // full capture + save completed
  | 'failed'           // error during capture/save/hardware
  | 'closed'           // user manually closed camera
  | 'permission_denied'
  | 'hardware_error'
  | 'metadata_timeout'
  | 'blank_screen'
  | 'not_supported'
  | 'orietation_error'
  | 'orientation_granted'
  | 'orientation_denied'

// Update CaptureSessionLog
export interface CaptureSessionLog {
  startedAt: string;
  endedAt: string;
  durationMs: number;
  outcome: CaptureOutcome;   // ← updated
  deviceMemory: number | null
  logs: CameraLogEntry[];
}

// ════════════════════════════════════════════════════════════
//  CAMERA STATE MODEL
// ════════════════════════════════════════════════════════════

export type CameraStep =
  | 'idle'
  | 'checking_orientation'
  | 'checking_support'
  | 'opening'
  | 'loading_metadata'
  | 'checking_blank'
  | 'ready'
  | 'capturing'
  | 'saving'
  | 'error'
  | 'fallback_native';

export interface CameraStateModel {
  // ── Step flags ─────────────────────────────────────────────
  currentStep: CameraStep;

  // ── Support / Permission ────────────────────────────────────
  isSupported: boolean;
  permissionDenied: boolean;

  // ── Hardware / Load ─────────────────────────────────────────
  hardwareError: boolean;
  metadataLoaded: boolean;
  blankScreen: boolean;

  // ── Capture / Save ──────────────────────────────────────────
  captureFailed: boolean;
  saveFailed: boolean;
  totalCapturesPerOpenCameraSession: number;

  // ── Session health ──────────────────────────────────────────
  retryCount: number;
  lastError: string | null;
  crashDetected: boolean;
}

export const INITIAL_CAMERA_STATE: CameraStateModel = {
  currentStep: 'idle',
  isSupported: false,
  permissionDenied: false,
  hardwareError: false,
  metadataLoaded: false,
  blankScreen: false,
  captureFailed: false,
  saveFailed: false,
  totalCapturesPerOpenCameraSession: 0,
  retryCount: 0,
  lastError: null,
  crashDetected: false,
};

// ── Local Storage key ────────────────────────────────────────
const LS_KEY = 'custm_camera_Active';

// ── Log entry shape ──────────────────────────────────────────
export interface CameraLogEntry {
  timestamp: string;
  step: CameraStep | string;
  errorType: string | null;
  message: string;
  stateSnapshot: Partial<CameraStateModel>;
  userAgent: string;
}

// ════════════════════════════════════════════════════════════
//  SERVICE
// ════════════════════════════════════════════════════════════

@Injectable({ providedIn: 'root' })
export class CameraStateService {
  // ── NEW: Log buffer ──────────────────────────────────────────   
  private logBuffer: CameraLogEntry[] = [];
  private captureStartedAt: number = 0;

  private _state: CameraStateModel = { ...INITIAL_CAMERA_STATE };
  componentName: string = 'CameraStateService';

  // ── Public read ──────────────────────────────────────────────
  get state(): Readonly<CameraStateModel> { return this._state; }

  constructor(
    public loggingService: LoggingService,

  ) {
    wrapAllMethodsWithLogging(this, this.componentName);
  }

  // ────────────────────────────────────────────────────────────
  //  INIT — run once per component lifecycle
  // ────────────────────────────────────────────────────────────
  init(): void {
    this._state = { ...INITIAL_CAMERA_STATE };
    this.checkCrashDetection();
  }

  // ════════════════════════════════════════════════════════════
  //  STEP 1 — Support check
  // ════════════════════════════════════════════════════════════
  markCheckingSupport(): void {
    this.patch({ currentStep: 'checking_support' });
    this.log('checking_support', null, 'Checking getUserMedia support');
  }

  markSupported(): void {
    this.captureStartedAt = Date.now();
    this.patch({ isSupported: true });
    this.log('checking_support', null, 'getUserMedia is supported');
  }

  markNotSupported(): void {
    this.captureStartedAt = Date.now();
    this.patch({ isSupported: false, currentStep: 'fallback_native' });
    this.log('checking_support', 'NOT_SUPPORTED',
      'getUserMedia not available — falling back to native camera');
    this.flushCaptureLog('not_supported');
  }

  markOrientationNotSupported(): void {
    this.captureStartedAt = Date.now();
    this.log('checking_orientation', null,
      'Orientation permission — non-iOS device, no permission required');
  }
  markOrientationError(err: any): void {
    this.captureStartedAt = Date.now();
    this.log('checking_orientation', 'ORIENTATION_ERROR',
      `iOS orientation permission request threw an error: ${err?.message}`, err);
    this.flushCaptureLog('orietation_error');
  }

  markOrientationGranted(): void {
    this.captureStartedAt = Date.now();
    this.log('checking_orientation', null,
      'iOS orientation permission granted — auto-rotate enabled');
  }

  markOrientationDenied(): void {
    this.captureStartedAt = Date.now();
    this.log('checking_orientation', 'ORIENTATION_DENIED',
      `iOS orientation permission denied — auto-rotate disabled`);
    this.flushCaptureLog('orientation_denied');
  }
  // ════════════════════════════════════════════════════════════
  //  STEP 2 — Open camera
  // ════════════════════════════════════════════════════════════
  // markOpening(): void {
  //   this.patch({ currentStep: 'opening', permissionDenied: false, hardwareError: false });
  //   this.log('opening', null, 'Attempting to open camera stream');
  // }

  markOpening(): void {
    //  Start fresh buffer from the moment camera opens
    // this.logBuffer = [];
    console.log("this.logBuffer", this.logBuffer)
    this.captureStartedAt = Date.now();

    this.patch({ currentStep: 'opening', permissionDenied: false, hardwareError: false });
    this.log('opening', null, 'Attempting to open camera stream');
  }

  markStreamOpened(): void {
    this.log('opening', null, 'Camera stream opened successfully');
  }

  markPermissionDenied(err: any): void {
    this.patch({ permissionDenied: true, currentStep: 'error', lastError: err?.message ?? 'Permission denied' });
    this.log('opening', 'NotAllowedError', 'Camera permission denied by user', err);
    this.flushCaptureLog('permission_denied');
  }

  markHardwareError(err: any): void {
    this.patch({ hardwareError: true, currentStep: 'error', lastError: err?.message ?? 'Hardware error' });
    this.log('opening', err?.name ?? 'HardwareError', `Camera hardware error: ${err?.message}`, err);
    this.flushCaptureLog('hardware_error');
  }

  markOpenRetry(attempt: number): void {
    this.patch({ retryCount: attempt });
    this.log('opening', null, `Retrying camera open — attempt ${attempt}`);
  }

  // ════════════════════════════════════════════════════════════
  //  STEP 3 — Metadata load
  // ════════════════════════════════════════════════════════════
  markLoadingMetadata(): void {
    this.patch({ currentStep: 'loading_metadata', metadataLoaded: false });
    this.log('loading_metadata', null, 'Waiting for video metadata to load');
  }

  markMetadataLoaded(width: number, height: number): void {
    this.patch({ metadataLoaded: true });
    this.log('loading_metadata', null, `Metadata loaded — video dimensions ${width}×${height}`);
  }

  markMetadataTimeout(): void {
    this.patch({ metadataLoaded: false, lastError: 'Metadata load timeout' });
    this.log('loading_metadata', 'METADATA_TIMEOUT', 'Video metadata did not load within timeout');
    this.flushCaptureLog('metadata_timeout');
  }

  // ════════════════════════════════════════════════════════════
  //  STEP 4 — Blank screen check
  // ════════════════════════════════════════════════════════════
  markCheckingBlank(): void {
    this.patch({ currentStep: 'checking_blank' });
    this.log('checking_blank', null, 'Checking for blank/black video frame');
  }

  markBlankScreenOk(): void {
    this.patch({ blankScreen: false, currentStep: 'ready' });
    this.setSessionActive(true);
    this.log('checking_blank', null, 'Video frame is valid — camera ready');
  }

  markBlankScreen(): void {
    this.patch({ blankScreen: true, lastError: 'Blank video frame detected' });
    this.log('checking_blank', 'BLANK_SCREEN', 'Video frame appears blank (0 or no pixel variance)');
    this.flushCaptureLog('blank_screen');
  }

  // ════════════════════════════════════════════════════════════
  //  STEP 5 — Capture
  // ════════════════════════════════════════════════════════════
  // markCapturing(): void {
  //   this.patch({ currentStep: 'capturing', captureFailed: false });
  //   this.log('capturing', null, `Capture triggered — total so far: ${this._state.totalCaptures}`);
  // }
  markCapturing(): void {
    // No buffer reset here anymore
    this.patch({ currentStep: 'capturing', captureFailed: false });
    this.log('capturing', null, `Capture triggered — total so far: ${this._state.totalCapturesPerOpenCameraSession}`);
  }

  markCaptureSuccess(): void {
    console.log("this._state.totalCaptures", this._state.totalCapturesPerOpenCameraSession)
    this.patch({ totalCapturesPerOpenCameraSession: this._state.totalCapturesPerOpenCameraSession + 1, captureFailed: false });
    this.log('capturing', null, `Capture success — total: ${this._state.totalCapturesPerOpenCameraSession}`);
  }

  markCaptureFailed(err: any): void {
    this.patch({ captureFailed: true, currentStep: 'error', lastError: err?.message ?? 'Capture failed' });
    this.log('capturing', 'CAPTURE_FAILED', `Photo capture failed: ${err?.message}`, err);
    this.flushCaptureLog('failed');
  }

  // ════════════════════════════════════════════════════════════
  //  STEP 6 — Save
  // ════════════════════════════════════════════════════════════
  markSaving(): void {
    this.patch({ currentStep: 'saving', saveFailed: false });
    this.log('saving', null, 'Emitting captured image to parent');
  }

  markSaveSuccess(): void {
    this.patch({ saveFailed: false });
    this.log('saving', null, 'Image emitted successfully');
    // this.flushCaptureLog('success');
  }

  markSaveFailed(err: any): void {
    this.patch({ saveFailed: true, lastError: err?.message ?? 'Save failed' });
    this.log('saving', 'SAVE_FAILED', `Failed to emit/save image: ${err?.message}`, err);
    this.flushCaptureLog('failed');
  }

  // ════════════════════════════════════════════════════════════
  //  SESSION END
  // ════════════════════════════════════════════════════════════
  markSessionEnded(reason: string = 'normal'): void {
    this.setSessionActive(false);
    this.log('idle', null, `Camera session ended — reason: ${reason}`);
    this.patch({ currentStep: 'idle' });
    this.flushCaptureLog('closed');
  }

  // ════════════════════════════════════════════════════════════
  //  CRASH DETECTION
  // ════════════════════════════════════════════════════════════
  private checkCrashDetection(): void {
    const wasActive = localStorage.getItem(LS_KEY) === 'true';
    if (wasActive) {
      this.patch({ crashDetected: true });
      this.log('idle', 'CRASH_DETECTED',
        `custm_camera_Active was still TRUE on load — previous session may have crashed`);
      localStorage.removeItem(LS_KEY);
    }
  }

  // ════════════════════════════════════════════════════════════
  //  LOCAL STORAGE
  // ════════════════════════════════════════════════════════════
  private setSessionActive(active: boolean): void {
    if (active) {
      localStorage.setItem(LS_KEY, 'true');
    } else {
      localStorage.removeItem(LS_KEY);
    }
  }

  // ════════════════════════════════════════════════════════════
  //  HELPERS
  // ════════════════════════════════════════════════════════════
  private patch(partial: Partial<CameraStateModel>): void {
    this._state = { ...this._state, ...partial };
  }

  private flushCaptureLog(outcome: CaptureOutcome): void {
    // Guard: nothing to send OR buffer already flushed

    if (this.logBuffer.length === 0 || this.captureStartedAt === 0) return;

    const endedAt = Date.now();

    const payload: CaptureSessionLog = {
      startedAt: new Date(this.captureStartedAt).toISOString(),

      endedAt: new Date(endedAt).toISOString(),

      durationMs: endedAt - this.captureStartedAt,

      outcome,

      deviceMemory: (navigator as any).deviceMemory ?? null,

      logs: [...this.logBuffer],

    };
    this.logBuffer = [];

    this.captureStartedAt = 0;   // ← 0 means "no active session"

    console.log("payload", payload)
    this.loggingService.handleCustomCameraLogs(payload)
    // const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

    // const sent = navigator.sendBeacon(this.backendUrl, blob);

    // if (!sent) {

    //   console.log("payload", payload)

    // }

  }

  // Core structured logger
  // log(
  //   step: CameraStep | string,
  //   errorType: string | null,
  //   message: string,
  //   _errObj?: any
  // ): void {
  //   const entry: CameraLogEntry = {
  //     timestamp: new Date().toISOString(),
  //     step,
  //     errorType,
  //     message,
  //     stateSnapshot: {
  //       currentStep: this._state.currentStep,
  //       isSupported: this._state.isSupported,
  //       permissionDenied: this._state.permissionDenied,
  //       hardwareError: this._state.hardwareError,
  //       metadataLoaded: this._state.metadataLoaded,
  //       blankScreen: this._state.blankScreen,
  //       captureFailed: this._state.captureFailed,
  //       saveFailed: this._state.saveFailed,
  //       totalCaptures: this._state.totalCaptures,
  //       retryCount: this._state.retryCount,
  //       lastError: this._state.lastError,
  //       crashDetected: this._state.crashDetected,
  //     },
  //     userAgent: navigator.userAgent,
  //   };

  //   const prefix = errorType ? `[CAMERA][ERROR]` : `[CAMERA]`;
  //   const label = errorType ? `${step} | ${errorType}` : step;

  //   console.groupCollapsed(`${prefix} ${label} — ${message}`);
  //   console.log('Step     :', step);
  //   if (errorType) console.error('Error    :', errorType);
  //   console.log('Message  :', message);
  //   console.log('State    :', entry.stateSnapshot);
  //   console.log('Time     :', entry.timestamp);
  //   console.log('UA       :', entry.userAgent);
  //   console.groupEnd();
  // }
  log(
    step: CameraStep | string,
    errorType: string | null,
    message: string,
    _errObj?: any
  ): void {

    const entry: CameraLogEntry = {
      timestamp: new Date().toISOString(),
      step,
      errorType,
      message,
      stateSnapshot: {
        currentStep: this._state.currentStep,
        isSupported: this._state.isSupported,
        permissionDenied: this._state.permissionDenied,
        hardwareError: this._state.hardwareError,
        metadataLoaded: this._state.metadataLoaded,
        blankScreen: this._state.blankScreen,
        captureFailed: this._state.captureFailed,
        saveFailed: this._state.saveFailed,
        totalCapturesPerOpenCameraSession: this._state.totalCapturesPerOpenCameraSession,
        retryCount: this._state.retryCount,
        lastError: this._state.lastError,
        crashDetected: this._state.crashDetected,
      },
      userAgent: navigator.userAgent,
    };

    // Push to buffer if a capture is in progress
    if (this.captureStartedAt > 0) {
      this.logBuffer.push(entry);
    }

    // Console output unchanged
    const prefix = errorType ? `[CAMERA][ERROR]` : `[CAMERA]`;
    const label = errorType ? `${step} | ${errorType}` : step;
    // console.groupCollapsed(`${prefix} ${label} — ${message}`);
    // console.log('Step     :', step);
    if (errorType) console.error('Error    :', errorType);
    // console.log('Message  :', message);
    // console.log('State    :', entry.stateSnapshot);
    // console.log('Time     :', entry.timestamp);
    console.groupEnd();
  }
}
